import { create } from 'zustand';

import { BizyPageAction, PageActionSchema, PageActionType } from './types';

interface ActionSchemaRegistryState {
	schemas: Map<string, PageActionSchema>;
	registerSchema: (pageId: string, schema: PageActionSchema) => void;
	unregisterSchema: (pageId: string) => void;
	getSchema: (pageId: string) => PageActionSchema | undefined;
}

/**
 * Global registry for page action schemas.
 * Pages register their schemas to declare how they handle params.
 */
export const useActionSchemaRegistry = create<ActionSchemaRegistryState>((set, get) => ({
	schemas: new Map(),

	registerSchema: (pageId, schema) => {
		set((state) => {
			const newSchemas = new Map(state.schemas);
			newSchemas.set(pageId, schema);
			return { schemas: newSchemas };
		});
	},

	unregisterSchema: (pageId) => {
		set((state) => {
			const newSchemas = new Map(state.schemas);
			newSchemas.delete(pageId);
			return { schemas: newSchemas };
		});
	},

	getSchema: (pageId) => {
		return get().schemas.get(pageId);
	},
}));

/**
 * Generic action executor that reads page schemas and handles actions.
 */
export async function executePageAction(
	action: BizyPageAction,
	schema: PageActionSchema | undefined,
	navigate: (path: string | { pathname?: string; search?: string }) => void
): Promise<void> {
	if (action.action !== PageActionType.UpdateReportFilters) {
		console.warn('[Bizy Action] Unknown action type:', action.action);
		return;
	}

	// Drop actions when schema is not registered (e.g., user navigated away)
	if (!schema) {
		console.warn('[Bizy Action] No schema registered for page action, ignoring');
		return;
	}

	let targetPath: string | null = null;
	const queryParamUpdates: Record<string, string | null | undefined> = {};

	// Handle report_view (top-level field, not in params)
	if (action.report_view && schema.allowedPathParams?.report_view) {
		const pathMapping = schema.allowedPathParams.report_view;
		targetPath = pathMapping[action.report_view];
		if (!targetPath) {
			console.warn(`[Bizy Action] report_view has value "${action.report_view}" but no path mapping found`);
		}
	}

	// Process params (these are query params like start_date, end_date, etc.)
	Object.entries(action.params ?? {}).forEach(([key, value]) => {
		// Check if this is a query param
		if (!schema.allowedQueryParams || schema.allowedQueryParams.includes(key)) {
			// Store null/undefined as-is to signal deletion; convert others to string
			if (value === null || value === undefined) {
				queryParamUpdates[key] = value;
			} else {
				queryParamUpdates[key] = String(value);
			}
			return;
		}

		// Unknown param - log warning but don't fail
		console.warn(`[Bizy Action] Unknown param: ${key} (not in schema)`);
	});

	// Build query string
	const searchParams = new URLSearchParams(window.location.search);
	Object.entries(queryParamUpdates).forEach(([key, value]) => {
		if (value !== null && value !== undefined) {
			searchParams.set(key, value);
		} else {
			searchParams.delete(key);
		}
	});

	const queryString = searchParams.toString();
	const searchString = queryString ? `?${queryString}` : '';

	// Navigate using React Router
	if (targetPath && targetPath !== window.location.pathname) {
		// Path changed - full navigation
		const finalUrl = `${targetPath}${searchString}`;
		navigate(finalUrl);
	} else {
		// Just update query params - use navigate with search object
		const currentPath = window.location.pathname;
		navigate({
			pathname: currentPath,
			search: searchString,
		});
	}
}
